/**
 * Platform Notifications Service
 * 
 * Hybrid approach: Single notification docs + user-level read tracking
 * Optimized for scale (70+ clients) with minimal Firestore operations
 */

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { logger } from '../../utils/logger';
import { createAuditLog } from '../audit/auditLog.service';
import type {
  PlatformNotification,
  CreateNotificationInput,
  UpdateNotificationInput,
  NotificationPreferences,
  NotificationDisplayItem
} from '../../types/notification.types';

const NOTIFICATIONS_COLLECTION = 'platform_notifications';
const USERS_COLLECTION = 'users';
const MAX_DISMISSED = 50;

const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);

/**
 * Create a new platform notification (Admin only)
 */
export const createNotification = async (
  input: CreateNotificationInput,
  creatorId: string,
  creatorName: string
): Promise<string | null> => {
  try {
    const notificationData = {
      title: input.title,
      message: input.message,
      type: input.type,
      priority: input.priority,
      targetAudience: input.targetAudience,
      createdBy: creatorId,
      createdByName: creatorName,
      createdAt: serverTimestamp(),
      updatedAt: null,
      expiresAt: input.expiresAt ? Timestamp.fromDate(input.expiresAt) : null,
      actionText: input.actionText || null,
      actionUrl: input.actionUrl || null,
      active: true
    };

    const docRef = await addDoc(notificationsRef, notificationData);
    logger.info(`Notification created: ${docRef.id}`);

    await createAuditLog({
      actorUserId: creatorId,
      actorRole: 'admin',
      action: 'SETTINGS_notification_create',
      severity: 'info',
      context: {
        notificationId: docRef.id,
        title: input.title,
        type: input.type,
        audience: input.targetAudience,
        createdByName: creatorName,
      },
    });

    return docRef.id;
  } catch (error) {
    logger.error('Error creating notification:', error);
    return null;
  }
};

/**
 * Update an existing notification (Admin only)
 */
export const updateNotification = async (
  notificationId: string,
  input: UpdateNotificationInput
): Promise<boolean> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    
    const updateData: Record<string, any> = {
      updatedAt: serverTimestamp()
    };

    if (input.title !== undefined) updateData.title = input.title;
    if (input.message !== undefined) updateData.message = input.message;
    if (input.type !== undefined) updateData.type = input.type;
    if (input.priority !== undefined) updateData.priority = input.priority;
    if (input.targetAudience !== undefined) updateData.targetAudience = input.targetAudience;
    if (input.expiresAt !== undefined) {
      updateData.expiresAt = input.expiresAt ? Timestamp.fromDate(input.expiresAt) : null;
    }
    if (input.actionText !== undefined) updateData.actionText = input.actionText || null;
    if (input.actionUrl !== undefined) updateData.actionUrl = input.actionUrl || null;
    if (input.active !== undefined) updateData.active = input.active;

    await updateDoc(docRef, updateData);
    logger.info(`Notification updated: ${notificationId}`);

    await createAuditLog({
      actorRole: 'admin',
      action: 'SETTINGS_notification_update',
      severity: 'info',
      context: {
        notificationId,
        updatedFields: Object.keys(updateData).filter(k => k !== 'updatedAt'),
      },
    });

    return true;
  } catch (error) {
    logger.error('Error updating notification:', error);
    return false;
  }
};

/**
 * Delete a notification (Creator only)
 */
export const deleteNotification = async (
  notificationId: string,
  userId: string
): Promise<boolean> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      logger.warn(`Notification not found: ${notificationId}`);
      return false;
    }

    const data = docSnap.data();
    if (data.createdBy !== userId) {
      logger.warn(`User ${userId} not authorized to delete notification ${notificationId}`);
      return false;
    }

    await deleteDoc(docRef);
    logger.info(`Notification deleted: ${notificationId}`);

    await createAuditLog({
      actorUserId: userId,
      actorRole: 'admin',
      action: 'SETTINGS_notification_delete',
      severity: 'warning',
      context: {
        notificationId,
        title: data.title,
      },
    });

    return true;
  } catch (error) {
    logger.error('Error deleting notification:', error);
    return false;
  }
};

/**
 * Get all notifications for admin management view
 */
export const getAllNotifications = async (): Promise<PlatformNotification[]> => {
  try {
    const q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PlatformNotification));
  } catch (error) {
    logger.error('Error getting all notifications:', error);
    return [];
  }
};

/**
 * Get active notifications for a user (filtered by role and preferences)
 */
export const getNotificationsForUser = async (
  userId: string,
  userRole: 'admin' | 'client',
  preferences: NotificationPreferences | null,
  userCreatedAt?: Date
): Promise<NotificationDisplayItem[]> => {
  try {
    const now = new Date();
    
    // Query active notifications targeting this user's role or 'all'
    const q = query(
      notificationsRef,
      where('active', '==', true),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const snapshot = await getDocs(q);
    const notifications: NotificationDisplayItem[] = [];
    
    const lastReadAt = preferences?.lastReadAt?.toDate() || new Date(0);
    const dismissed = preferences?.dismissed || [];
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      
      // Filter by audience
      if (data.targetAudience !== 'all' && data.targetAudience !== (userRole === 'admin' ? 'admins' : 'clients')) {
        return;
      }
      
      // Filter expired
      if (data.expiresAt && data.expiresAt.toDate() < now) {
        return;
      }

      // Get notification created date - skip if invalid (don't default to current time)
      const createdAtTimestamp = data.createdAt;
      if (!createdAtTimestamp) {
        // Skip notifications without createdAt - they're invalid
        return;
      }

      // Convert to Date object
      const createdAt = createdAtTimestamp.toDate ? createdAtTimestamp.toDate() : null;
      if (!createdAt || !(createdAt instanceof Date) || isNaN(createdAt.getTime())) {
        // Skip notifications with invalid dates
        return;
      }

      // Filter by user creation date (new users shouldn't see old notifications)
      if (userCreatedAt) {
        // Ensure userCreatedAt is valid
        const userCreated = userCreatedAt instanceof Date && !isNaN(userCreatedAt.getTime()) ? userCreatedAt : null;
        
        if (!userCreated) {
          // If userCreatedAt is invalid, skip this notification to be safe
          return;
        }
        
        // Filter out notifications created before user account was created
        if (createdAt < userCreated) {
          return;
        }
      }
      
      const isUnread = createdAt > lastReadAt && !dismissed.includes(doc.id);
      
      notifications.push({
        id: doc.id,
        ...data,
        isUnread
      } as NotificationDisplayItem);
    });
    
    return notifications;
  } catch (error) {
    logger.error('Error getting notifications for user:', error);
    return [];
  }
};

/**
 * Get unread notification count for badge
 */
export const getUnreadCount = async (
  userId: string,
  userRole: 'admin' | 'client',
  preferences: NotificationPreferences | null,
  userCreatedAt?: Date
): Promise<number> => {
  try {
    const notifications = await getNotificationsForUser(userId, userRole, preferences, userCreatedAt);
    return notifications.filter(n => n.isUnread).length;
  } catch (error) {
    logger.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Mark all notifications as read (updates lastReadAt timestamp)
 */
export const markAllAsRead = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      'notificationPreferences.lastReadAt': serverTimestamp()
    });
    logger.info(`All notifications marked as read for user: ${userId}`);
    return true;
  } catch (error) {
    logger.error('Error marking all as read:', error);
    return false;
  }
};

/**
 * Dismiss a specific notification (adds to dismissed array)
 */
export const dismissNotification = async (
  userId: string,
  notificationId: string
): Promise<boolean> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      logger.warn(`User not found: ${userId}`);
      return false;
    }
    
    const userData = userSnap.data();
    const currentDismissed = userData.notificationPreferences?.dismissed || [];
    
    // Don't add if already dismissed
    if (currentDismissed.includes(notificationId)) {
      return true;
    }
    
    // Cap at MAX_DISMISSED (remove oldest if needed)
    let newDismissed = [...currentDismissed, notificationId];
    if (newDismissed.length > MAX_DISMISSED) {
      newDismissed = newDismissed.slice(-MAX_DISMISSED);
    }
    
    await updateDoc(userRef, {
      'notificationPreferences.dismissed': newDismissed
    });
    
    logger.info(`Notification ${notificationId} dismissed by user: ${userId}`);
    return true;
  } catch (error) {
    logger.error('Error dismissing notification:', error);
    return false;
  }
};

/**
 * Get user's notification preferences
 */
export const getUserNotificationPreferences = async (
  userId: string
): Promise<NotificationPreferences | null> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return null;
    }
    
    const userData = userSnap.data();
    return userData.notificationPreferences || {
      lastReadAt: null,
      dismissed: []
    };
  } catch (error) {
    logger.error('Error getting notification preferences:', error);
    return null;
  }
};

/**
 * Initialize notification preferences for a user (if not exists)
 */
export const initializeUserPreferences = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return false;
    }
    
    const userData = userSnap.data();
    if (!userData.notificationPreferences) {
      await updateDoc(userRef, {
        notificationPreferences: {
          lastReadAt: null,
          dismissed: []
        }
      });
    }
    
    return true;
  } catch (error) {
    logger.error('Error initializing user preferences:', error);
    return false;
  }
};

